import { type RootState, useAppDispatch } from "@store";
import { updateStoryByKeys } from "@store/story";
import { isLoadedDocument } from "@store/story/selectors";
import { produce } from "immer";
import { useStore } from "react-redux";
import { toast } from "react-toastify";
import type { WholeStoryType } from "@/config";
import { useUpdatePageMutation } from "@/shared/services";
import { useGeneratePreviewLink } from "./use-generate-preview-link";
import { useUploadMetadata } from "./use-upload-metadata";

export const useUpdatePreviewAndMeta = () => {
  const { mutateAsync: updatePage } = useUpdatePageMutation();
  const handleUploadMetadata = useUploadMetadata();
  const handlePreviewLink = useGeneratePreviewLink();
  const dispatch = useAppDispatch();
  const reduxStore = useStore<RootState>();

  const updatePreviewAndMeta = async ({
    story,
    skipUpdate,
    versionId,
    skipHistoryUpdate,
    versionData,
  }: {
    story: WholeStoryType;
    skipUpdate: boolean;
    versionId?: string;
    skipHistoryUpdate?: boolean;
    versionData?: Record<string, unknown> | null;
  }) => {
    const preview = await handlePreviewLink(
      story,
      versionId,
      true,
      versionData,
    ).catch((e) => {
      console.error("Error generating preview:", e);
      return null;
    });

    if (!preview) {
      toast.error("An error occurred while updating your story");
      return;
    }

    const meta = await handleUploadMetadata(
      Object.assign(story, { previewUrl: preview }),
      versionId,
    ).catch(() => null);

    if (!meta) {
      toast.error("An error occurred while updating your story");
      console.error(preview, meta);
      return;
    }

    const produced = produce(story, (draft) => {
      draft.layoutVersion = window.BUNDLE_VERSION;
      draft.previewUrl = preview;
      draft.settings.contentPointer = meta.url;
      draft.slides = [];

      if (Array.isArray(draft.history) || !draft.history) {
        draft.history = {};
      }

      const sessionId = meta.sessionId;

      const session = draft.history[sessionId] || {
        userIds: [],
        url: meta.url,
        revisions: 0,
      };

      if (!session.userIds.includes(meta.userId)) {
        session.userIds.push(meta.userId);
      }

      session.revisions += 1;
      session.url = meta.url;
      if (!skipHistoryUpdate) {
        draft.history[sessionId] = session;
        draft.lastSessionId = sessionId;
      }
    });
    // The `history` map is persisted with the NEXT updatePage from Redux, so
    // it must only be mirrored into the document this save belongs to — the
    // store may already hold the other side of a template <-> version
    // navigation by the time the uploads finish.
    if (
      isLoadedDocument(reduxStore.getState(), { storyId: story._id, versionId })
    )
      dispatch(
        updateStoryByKeys({
          data: { history: produced.history },
          autosave: false,
        }),
      );
    if (skipUpdate) return produced;
    return await updatePage({
      id: produced._id,
      data: produced,
      skipInvalidation: true,
    });
  };

  return updatePreviewAndMeta;
};
